import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import FeatureVideoCallout from 'containers/feature-video-callout/FeatureVideoCallout'
import { CORRECTIONS } from 'routes/routeConstant'
import { SendVariant } from './logic'

export type SendPhase = 'idle' | 'sending' | 'saving' | 'saved' | 'saved-not-sent'

export const InfoIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 8h.01M11 12h1v5h1' />
  </svg>
)

const GiftIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <rect x='3' y='8' width='18' height='4' rx='1' />
    <path d='M5 12v8h14v-8M12 8v12M12 8c-2-4-6-4-6-1s6 1 6 1zm0 0c2-4 6-4 6-1s-6 1-6 1z' />
  </svg>
)

interface SendAreaProps {
  variant: SendVariant
  /** Something to send or save: an answer, a recording, or both. */
  ready: boolean
  phase: SendPhase
  onSend: () => void
  onSave: () => void
  /** Locked variant: the Pro upsell callout on "Send to a teacher". */
  calloutOpen: boolean
  onProTap: () => void
  onCalloutDismiss: (e?: React.SyntheticEvent) => void
  error?: string
}

const SendButton: React.FC<{ label: string; phase: SendPhase; ready: boolean; onClick: () => void }> = ({
  label,
  phase,
  ready,
  onClick,
}) => {
  const { t } = useTranslation('writeSpeak')
  const sending = phase === 'sending'
  return (
    <button
      type='button'
      className={`ws-btn${sending ? ' sending' : ''}`}
      onClick={onClick}
      disabled={!ready || sending || phase === 'saving'}
      aria-busy={sending}
    >
      {sending ? <span className='ws-spin' role='img' aria-label={t('send.sending')} /> : label}
    </button>
  )
}

const SaveButton: React.FC<{ label: string; phase: SendPhase; ready: boolean; onClick: () => void }> = ({
  label,
  phase,
  ready,
  onClick,
}) => {
  const { t } = useTranslation('writeSpeak')
  const saving = phase === 'saving'
  const saved = phase === 'saved'
  return (
    <button
      type='button'
      className={`ws-btn${saving ? ' sending' : ''}`}
      onClick={onClick}
      disabled={!ready || saving || saved || phase === 'sending'}
      aria-busy={saving}
    >
      {saving ? <span className='ws-spin' role='img' aria-label={t('send.saving')} /> : saved ? t('send.saved') : label}
    </button>
  )
}

/** The one place the step differs by plan (plan section 03). */
const SendArea: React.FC<SendAreaProps> = ({
  variant,
  ready,
  phase,
  onSend,
  onSave,
  calloutOpen,
  onProTap,
  onCalloutDismiss,
  error,
}) => {
  const { t } = useTranslation('writeSpeak')
  const errorNotice = error ? (
    <div className='ws-notice' role='alert' style={{ marginTop: 12 }}>
      <InfoIcon />
      <div>{error}</div>
    </div>
  ) : null

  if (variant === 'corrected') {
    return (
      <div className='ws-sendbar light'>
        <div className='copy'>
          <b>{t('send.corrected.title')}</b>
          <span>{t('send.corrected.body')}</span>
        </div>
        <div className='acts'>
          <Link className='ws-btn' to={CORRECTIONS}>
            {t('send.corrected.cta')}
          </Link>
        </div>
      </div>
    )
  }

  if (variant === 'waiting') {
    return (
      <div className='ws-sendbar light'>
        <div className='copy'>
          <b>{t('send.waiting.title')}</b>
          <span>{t('send.waiting.body')}</span>
        </div>
        <div className='acts'>
          <Link className='ws-btn ghost' to={CORRECTIONS}>
            {t('send.waiting.cta')}
          </Link>
        </div>
      </div>
    )
  }

  if (variant === 'open') {
    return (
      <>
        <div className='ws-sendbar'>
          <div className='copy'>
            <b>{t('send.open.title')}</b>
            <span>{t('send.open.body')}</span>
          </div>
          <div className='acts'>
            <SendButton label={t('send.open.cta')} phase={phase} ready={ready} onClick={onSend} />
          </div>
        </div>
        {errorNotice}
      </>
    )
  }

  if (variant === 'pro') {
    return (
      <>
        <div className='ws-sendbar'>
          <div className='copy'>
            <span>{t('send.pro.body')}</span>
          </div>
          <div className='acts'>
            <button
              type='button'
              className='ws-link'
              onClick={onSave}
              disabled={!ready || phase === 'saving' || phase === 'sending' || phase === 'saved-not-sent'}
            >
              {phase === 'saved-not-sent'
                ? t('send.pro.savedNotSent')
                : phase === 'saving'
                  ? t('send.pro.savingEllipsis')
                  : t('send.pro.saveWithoutSending')}
            </button>
            <SendButton label={t('send.pro.cta')} phase={phase} ready={ready} onClick={onSend} />
          </div>
        </div>
        {errorNotice}
      </>
    )
  }

  if (variant === 'pro-capped') {
    return (
      <>
        <div className='ws-sendbar light'>
          <div className='copy'>
            <b>{t('send.capped.title')}</b>
            <span>{t('send.capped.body')}</span>
          </div>
          <div className='acts'>
            <SaveButton label={t('send.capped.cta')} phase={phase} ready={ready} onClick={onSave} />
          </div>
        </div>
        {errorNotice}
      </>
    )
  }

  if (variant === 'taster') {
    return (
      <>
        <div className='ws-notice gift'>
          <GiftIcon />
          <div>
            <Trans t={t} i18nKey='send.taster.gift' components={{ b: <b /> }} />
          </div>
        </div>
        <div className='ws-sendbar'>
          <div className='copy'>
            <b>{t('send.taster.title')}</b>
            <span>{t('send.taster.body')}</span>
          </div>
          <div className='acts'>
            <SendButton label={t('send.taster.cta')} phase={phase} ready={ready} onClick={onSend} />
          </div>
        </div>
        {errorNotice}
      </>
    )
  }

  // Free, Standard and Standard trial.
  return (
    <>
      <div className={`ws-sendbar light${calloutOpen ? ' glow' : ''}`}>
        <div className='copy'>
          <b>{t('send.locked.title')}</b>
          <span>{t('send.locked.body')}</span>
        </div>
        <div className='acts'>
          <span className='ws-callout-anchor'>
            {calloutOpen && (
              <FeatureVideoCallout
                title={t('send.locked.calloutTitle')}
                description={t('send.locked.calloutDescription')}
                note={t('common:teacherLanguageNotice')}
                videoSrc='/videos/writing-guide.mp4'
                videoLabel={t('send.locked.calloutVideoLabel')}
                onDismiss={onCalloutDismiss}
                isLockedFeature
                arrowAlign='right'
              />
            )}
            <button
              type='button'
              className='ws-btn ghost'
              onClick={onProTap}
              aria-expanded={calloutOpen}
              aria-haspopup='dialog'
            >
              {t('send.locked.sendToTeacher')} <span className='ws-pro'>{t('send.locked.proBadge')}</span>
            </button>
          </span>
          <SaveButton label={t('send.locked.cta')} phase={phase} ready={ready} onClick={onSave} />
        </div>
      </div>
      {errorNotice}
    </>
  )
}

export default SendArea
